/**
 * Admin Payroll - Commission payment processing
 *
 * Admin can view client payment status, mark commissions as paid,
 * defer payments, and manage monthly payroll processing.
 */
import { useState, useEffect, useMemo } from 'react'
import {
  PoundSterling,
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertTriangle,
  Building2,
  User,
  ChevronDown,
  ChevronUp,
  Check,
  ArrowRight,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  getPayrollSummary,
  getSalespersonPayrollDetail,
  markCommissionsPaid,
  deferCommissions,
  getClientPayments,
  bulkUpdateClientPayments,
} from '@/services/salesApi'
import type {
  PayrollMonthSummary,
  SalespersonPayrollDetail,
  ClientPayment,
  SalespersonCommissionSummary,
} from '@/types'

export default function Payroll() {
  const [summary, setSummary] = useState<PayrollMonthSummary | null>(null)
  const [expandedSalesperson, setExpandedSalesperson] = useState<number | null>(null)
  const [salespersonDetail, setSalespersonDetail] = useState<SalespersonPayrollDetail | null>(null)
  const [clientPayments, setClientPayments] = useState<ClientPayment[]>([])
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Selection for bulk actions
  const [selectedCommissions, setSelectedCommissions] = useState<number[]>([])
  const [selectedPayments, setSelectedPayments] = useState<number[]>([])

  // View mode: 'salespeople' or 'payments'
  const [viewMode, setViewMode] = useState<'salespeople' | 'payments'>('salespeople')

  // Defer modal
  const [showDeferModal, setShowDeferModal] = useState(false)
  const [deferMonth, setDeferMonth] = useState(1)
  const [deferYear, setDeferYear] = useState(new Date().getFullYear())

  // Current month/year for filtering
  const now = new Date()
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())

  useEffect(() => {
    fetchData()
  }, [selectedMonth, selectedYear])

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    setExpandedSalesperson(null)
    setSalespersonDetail(null)
    setSelectedCommissions([])
    setSelectedPayments([])

    try {
      const [summaryData, paymentsData] = await Promise.all([
        getPayrollSummary(selectedMonth, selectedYear),
        getClientPayments(selectedMonth, selectedYear),
      ])
      setSummary(summaryData)
      setClientPayments(paymentsData)
    } catch {
      setError('Failed to load payroll data')
    } finally {
      setLoading(false)
    }
  }

  const loadSalespersonDetail = async (salespersonId: number) => {
    if (expandedSalesperson === salespersonId) {
      setExpandedSalesperson(null)
      setSalespersonDetail(null)
      return
    }

    setDetailLoading(true)
    setExpandedSalesperson(salespersonId)

    try {
      const detail = await getSalespersonPayrollDetail(salespersonId, selectedMonth, selectedYear)
      setSalespersonDetail(detail)
    } catch {
      toast.error('Failed to load commission details')
    } finally {
      setDetailLoading(false)
    }
  }

  const handleMarkPaid = async () => {
    if (selectedCommissions.length === 0) return

    try {
      await markCommissionsPaid(selectedCommissions)
      toast.success(`Marked ${selectedCommissions.length} commission(s) as paid`)
      setSelectedCommissions([])
      fetchData()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid')
    }
  }

  const handleDefer = async () => {
    if (selectedCommissions.length === 0) return

    try {
      await deferCommissions(selectedCommissions, deferMonth, deferYear)
      toast.success(`Deferred ${selectedCommissions.length} commission(s)`)
      setSelectedCommissions([])
      setShowDeferModal(false)
      fetchData()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to defer commissions')
    }
  }

  const handleBulkMarkPaymentsReceived = async (received: boolean) => {
    if (selectedPayments.length === 0) return

    try {
      await bulkUpdateClientPayments(selectedPayments, received)
      toast.success(`Updated ${selectedPayments.length} payment(s)`)
      setSelectedPayments([])
      fetchData()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to update payments')
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(amount)
  }

  const getMonthName = (month: number) => {
    return new Date(2000, month - 1, 1).toLocaleString('en-GB', { month: 'long' })
  }

  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12)
      setSelectedYear(selectedYear - 1)
    } else {
      setSelectedMonth(selectedMonth - 1)
    }
  }

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1)
      setSelectedYear(selectedYear + 1)
    } else {
      setSelectedMonth(selectedMonth + 1)
    }
  }

  // Filter payments by status
  const unpaidPayments = useMemo(
    () => clientPayments.filter((p) => !p.paymentReceived),
    [clientPayments]
  )
  const paidPayments = useMemo(
    () => clientPayments.filter((p) => p.paymentReceived),
    [clientPayments]
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 text-slate-500 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <p className="text-slate-300 mb-2">{error}</p>
        <button onClick={fetchData} className="btn-secondary text-sm">
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10">
            <PoundSterling className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Payroll</h1>
            <p className="text-slate-400">Process commission payments</p>
          </div>
        </div>
        <button onClick={fetchData} className="btn-secondary flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Month Selector */}
      <div className="card p-4">
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-400" />
            <span className="text-lg font-semibold text-white">
              {getMonthName(selectedMonth)} {selectedYear}
            </span>
          </div>
          <button
            onClick={goToNextMonth}
            className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <PoundSterling className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Total Commissions</span>
            </div>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary.totalCommissions)}</p>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-slate-400">Pending Client</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">{formatCurrency(summary.totalPending)}</p>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-slate-400">Ready to Pay</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">{formatCurrency(summary.totalReady)}</p>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-slate-400">Paid Out</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(summary.totalPaid)}</p>
          </div>
        </div>
      )}

      {/* View Mode Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setViewMode('salespeople')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            viewMode === 'salespeople'
              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
          }`}
        >
          <User className="w-4 h-4 inline-block mr-2" />
          By Salesperson
        </button>
        <button
          onClick={() => setViewMode('payments')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            viewMode === 'payments'
              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
          }`}
        >
          <Building2 className="w-4 h-4 inline-block mr-2" />
          Client Payments ({unpaidPayments.length} pending)
        </button>
      </div>

      {/* Salespeople View */}
      {viewMode === 'salespeople' && (
        <>
          {/* Bulk Actions */}
          {selectedCommissions.length > 0 && (
            <div className="card p-4 bg-blue-500/10 border-blue-500/30">
              <div className="flex items-center justify-between">
                <span className="text-blue-400">
                  {selectedCommissions.length} commission(s) selected
                </span>
                <div className="flex gap-2">
                  <button onClick={handleMarkPaid} className="btn-primary text-sm">
                    <Check className="w-4 h-4 mr-1" />
                    Mark Paid
                  </button>
                  <button
                    onClick={() => setShowDeferModal(true)}
                    className="btn-secondary text-sm"
                  >
                    <ArrowRight className="w-4 h-4 mr-1" />
                    Defer
                  </button>
                  <button
                    onClick={() => setSelectedCommissions([])}
                    className="btn-secondary text-sm"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Salespeople List */}
          {!summary?.salespeople?.length ? (
            <div className="card text-center py-12">
              <User className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-200 mb-2">No commission data</h3>
              <p className="text-slate-400">
                No commission records for {getMonthName(selectedMonth)} {selectedYear}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {summary.salespeople.map((sp) => (
                <SalespersonRow
                  key={sp.salespersonId}
                  salesperson={sp}
                  isExpanded={expandedSalesperson === sp.salespersonId}
                  onToggle={() => loadSalespersonDetail(sp.salespersonId)}
                  detailLoading={detailLoading && expandedSalesperson === sp.salespersonId}
                  detail={expandedSalesperson === sp.salespersonId ? salespersonDetail : null}
                  selectedCommissions={selectedCommissions}
                  onSelectCommission={(id, selected) => {
                    if (selected) {
                      setSelectedCommissions([...selectedCommissions, id])
                    } else {
                      setSelectedCommissions(selectedCommissions.filter((c) => c !== id))
                    }
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Client Payments View */}
      {viewMode === 'payments' && (
        <>
          {/* Bulk Actions */}
          {selectedPayments.length > 0 && (
            <div className="card p-4 bg-blue-500/10 border-blue-500/30">
              <div className="flex items-center justify-between">
                <span className="text-blue-400">{selectedPayments.length} payment(s) selected</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleBulkMarkPaymentsReceived(true)}
                    className="btn-primary text-sm"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Mark Received
                  </button>
                  <button
                    onClick={() => handleBulkMarkPaymentsReceived(false)}
                    className="btn-secondary text-sm"
                  >
                    Mark Not Received
                  </button>
                  <button onClick={() => setSelectedPayments([])} className="btn-secondary text-sm">
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Unpaid Payments */}
          <div className="card">
            <div className="p-4 border-b border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Awaiting Client Payment ({unpaidPayments.length})
              </h3>
            </div>
            {unpaidPayments.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-500/50 mx-auto mb-3" />
                <p className="text-slate-400">All client payments received!</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-700/50">
                {unpaidPayments.map((payment) => (
                  <PaymentRow
                    key={payment.id}
                    payment={payment}
                    selected={selectedPayments.includes(payment.id)}
                    onSelect={(selected) => {
                      if (selected) {
                        setSelectedPayments([...selectedPayments, payment.id])
                      } else {
                        setSelectedPayments(selectedPayments.filter((p) => p !== payment.id))
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Paid Payments */}
          {paidPayments.length > 0 && (
            <div className="card">
              <div className="p-4 border-b border-slate-700/50">
                <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Payment Received ({paidPayments.length})
                </h3>
              </div>
              <div className="divide-y divide-slate-700/50">
                {paidPayments.map((payment) => (
                  <PaymentRow
                    key={payment.id}
                    payment={payment}
                    selected={selectedPayments.includes(payment.id)}
                    onSelect={(selected) => {
                      if (selected) {
                        setSelectedPayments([...selectedPayments, payment.id])
                      } else {
                        setSelectedPayments(selectedPayments.filter((p) => p !== payment.id))
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Defer Modal */}
      {showDeferModal && (
        <div className="modal-overlay">
          <div className="modal-content p-6 w-full max-w-sm">
            <h2 className="text-lg font-semibold text-slate-100 mb-4">Defer Commissions</h2>
            <p className="text-slate-400 text-sm mb-4">
              Select the month to defer {selectedCommissions.length} commission(s) to.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="label">Month</label>
                <select
                  value={deferMonth}
                  onChange={(e) => setDeferMonth(parseInt(e.target.value))}
                  className="input"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>
                      {getMonthName(m)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Year</label>
                <select
                  value={deferYear}
                  onChange={(e) => setDeferYear(parseInt(e.target.value))}
                  className="input"
                >
                  {[now.getFullYear(), now.getFullYear() + 1].map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeferModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleDefer} className="btn-primary">
                Defer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function SalespersonRow({
  salesperson,
  isExpanded,
  onToggle,
  detailLoading,
  detail,
  selectedCommissions,
  onSelectCommission,
}: {
  salesperson: SalespersonCommissionSummary
  isExpanded: boolean
  onToggle: () => void
  detailLoading: boolean
  detail: SalespersonPayrollDetail | null
  selectedCommissions: number[]
  onSelectCommission: (id: number, selected: boolean) => void
}) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(amount)
  }

  return (
    <div className="card overflow-hidden">
      {/* Summary Row */}
      <div
        onClick={onToggle}
        className="p-4 cursor-pointer hover:bg-slate-800/30 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <User className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-100">{salesperson.salespersonName}</h3>
              <p className="text-sm text-slate-400">{salesperson.clientCount} client(s)</p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-sm text-slate-400">Total</p>
              <p className="text-lg font-bold text-white">{formatCurrency(salesperson.totalEarned)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-amber-400">Pending</p>
              <p className="font-medium text-amber-400">
                {formatCurrency(salesperson.pendingClientPayment)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-blue-400">Ready</p>
              <p className="font-medium text-blue-400">{formatCurrency(salesperson.readyToPay)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-emerald-400">Paid</p>
              <p className="font-medium text-emerald-400">{formatCurrency(salesperson.alreadyPaid)}</p>
            </div>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>
      </div>

      {/* Detail Rows */}
      {isExpanded && (
        <div className="border-t border-slate-700/50 bg-slate-800/20">
          {detailLoading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-6 h-6 text-slate-500 animate-spin mx-auto" />
            </div>
          ) : detail?.commissions?.length ? (
            <table className="w-full">
              <thead className="text-xs text-slate-500 uppercase">
                <tr className="border-b border-slate-700/50">
                  <th className="p-3 text-left w-10"></th>
                  <th className="p-3 text-left">Client</th>
                  <th className="p-3 text-left">Service</th>
                  <th className="p-3 text-left">Rate</th>
                  <th className="p-3 text-right">Amount</th>
                  <th className="p-3 text-left">Client Paid</th>
                  <th className="p-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {detail.commissions.map((comm, idx) => {
                  const isReady = comm.clientPaymentReceived && comm.commissionStatus === 'ready_to_pay'
                  return (
                    <tr key={idx} className="text-sm">
                      <td className="p-3">
                        {isReady && (
                          <input
                            type="checkbox"
                            checked={selectedCommissions.includes(comm.clientId)}
                            onChange={(e) => onSelectCommission(comm.clientId, e.target.checked)}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </td>
                      <td className="p-3 text-slate-200">{comm.clientName}</td>
                      <td className="p-3 text-slate-400">
                        {comm.serviceType === 'planning_recurring' ? 'Planning' : 'Website'}
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded bg-slate-700/50 text-xs text-slate-300">
                          {comm.rateTier === 'ten_percent'
                            ? '10%'
                            : comm.rateTier === 'five_percent'
                            ? '5%'
                            : '£120'}
                        </span>
                      </td>
                      <td className="p-3 text-right font-medium text-emerald-400">
                        {formatCurrency(comm.monthlyAmount)}
                      </td>
                      <td className="p-3">
                        {comm.clientPaymentReceived ? (
                          <span className="text-emerald-400 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Yes
                          </span>
                        ) : (
                          <span className="text-amber-400 flex items-center gap-1">
                            <Clock className="w-4 h-4" /> No
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <StatusBadge status={comm.commissionStatus} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-slate-400">No commission details available</div>
          )}
        </div>
      )}
    </div>
  )
}

function PaymentRow({
  payment,
  selected,
  onSelect,
}: {
  payment: ClientPayment
  selected: boolean
  onSelect: (selected: boolean) => void
}) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(amount)
  }

  return (
    <div className="p-4 flex items-center justify-between hover:bg-slate-800/30 transition-colors">
      <div className="flex items-center gap-4">
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
          className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-500"
        />
        <div className="p-2 rounded-lg bg-slate-700/50">
          <Building2 className="w-5 h-5 text-slate-400" />
        </div>
        <div>
          <h4 className="font-medium text-slate-200">{payment.clientName || 'Unknown Client'}</h4>
          {payment.notes && <p className="text-xs text-slate-500">{payment.notes}</p>}
        </div>
      </div>

      <div className="flex items-center gap-6">
        <div className="text-right">
          <p className="font-medium text-white">{formatCurrency(payment.amountExpected)}</p>
        </div>
        {payment.paymentReceived ? (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400">
            Received
          </span>
        ) : (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400">
            Pending
          </span>
        )}
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    pending_client_payment: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Pending Client' },
    ready_to_pay: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Ready' },
    paid: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Paid' },
    deferred: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'Deferred' },
  }

  const c = config[status] || config.pending_client_payment

  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${c.bg} ${c.text}`}>{c.label}</span>
  )
}
